import { forwardRef, useImperativeHandle, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { fetchApi } from "@/lib/api";
import {
  QuoteStatusEnum,
  PublicQuoteResponseSchema,
  type PublicQuote,
} from "@forgepoint/shared";
import { z } from "zod";

interface PublicQuoteSignatureSectionProps {
  quote: PublicQuote;
  onSignatureCreated: (quote: PublicQuote) => void;
}

/** Handle exposed by PublicQuoteSignatureSection via ref. */
export interface PublicQuoteSignatureSectionHandle {
  openSignDialog: () => void;
}

/**
 * Determines whether a quote is eligible for customer signing.
 * @param quote - The hydrated quote to check
 * @returns true if the quote can be signed by the customer
 */
export function canQuoteBeSigned(quote: PublicQuote): boolean {
  return (
    quote.status === QuoteStatusEnum.FINALIZED &&
    Boolean(quote.requiresCustomerSignature) &&
    Boolean(quote.isLatestRevision) &&
    !quote.isSigned
  );
}

export const PublicQuoteSignatureSection = forwardRef<
  PublicQuoteSignatureSectionHandle,
  PublicQuoteSignatureSectionProps
>(function PublicQuoteSignatureSection({ quote, onSignatureCreated }, ref) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [hasAgreed, setHasAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValidEmail = (email: string) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const canBeSigned = canQuoteBeSigned(quote);

  const trimmedSignerEmail = signerEmail.trim();
  const hasValidEmail =
    trimmedSignerEmail.length > 0 && isValidEmail(trimmedSignerEmail);

  const isSubmitDisabled =
    !hasAgreed || !signerName.trim() || !hasValidEmail || isSubmitting;

  const openDialog = () => {
    setError(null);
    setIsDialogOpen(true);
  };

  useImperativeHandle(ref, () => ({ openSignDialog: openDialog }));

  const closeDialog = () => {
    if (isSubmitting) {
      return;
    }
    setIsDialogOpen(false);
  };

  const handleSubmit = async () => {
    if (isSubmitDisabled) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetchApi(
        `public/quotes/${quote.id}/signature`,
        "POST",
        {
          body: {
            signerName: signerName.trim(),
            signerEmail: signerEmail.trim(),
          },
        },
      );

      if (!response.ok) {
        const errorResult = z
          .object({ error: z.string() })
          .safeParse(response.data);
        setError(
          errorResult.success
            ? errorResult.data.error
            : "We could not capture your signature. Please try again shortly.",
        );
        return;
      }

      const data = PublicQuoteResponseSchema.parse(response.data);
      onSignatureCreated(data.quote);
      setIsDialogOpen(false);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "We could not capture your signature. Please try again shortly.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!canBeSigned) {
    return null;
  }

  return (
    <div className="mt-8 print:hidden flex justify-center">
      <Button variant="default" size="lg" onClick={openDialog}>
        Accept and Sign
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Accept and Sign Quote</DialogTitle>
            <DialogDescription>
              Provide your details to confirm you accept the terms of this
              quote.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="signer-name">
                Name
              </label>
              <Input
                id="signer-name"
                value={signerName}
                onChange={(event) => setSignerName(event.target.value)}
                placeholder="Your full name"
              />
            </div>

            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">
                Signature preview
              </div>
              <div className="border rounded px-4 py-3 bg-white">
                <div className="relative font-signature text-3xl whitespace-nowrap overflow-hidden text-ellipsis">
                  <span className="invisible block">
                    {/* Non-breaking space keeps the signature line height when the name is empty */}
                    {signerName || "\u00A0"}
                  </span>
                  <span className="absolute inset-0">{signerName}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="signer-email">
                Email
              </label>
              <Input
                id="signer-email"
                type="email"
                value={signerEmail}
                onChange={(event) => setSignerEmail(event.target.value)}
                placeholder="you@example.com"
                aria-invalid={trimmedSignerEmail.length > 0 && !hasValidEmail}
              />
              {trimmedSignerEmail.length > 0 && !hasValidEmail && (
                <p className="text-xs text-red-600">
                  Please enter a valid email address.
                </p>
              )}
            </div>

            <div className="flex items-start space-x-2">
              <Checkbox
                id="agree-terms"
                checked={hasAgreed}
                onCheckedChange={(value) => setHasAgreed(Boolean(value))}
              />
              <label
                htmlFor="agree-terms"
                className="text-sm text-muted-foreground leading-snug"
              >
                I agree to the terms of this quote.
              </label>
            </div>

            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              type="button"
              onClick={closeDialog}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
            >
              {isSubmitting ? "Signing…" : "Sign Quote"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});
